import { useEffect, useState } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogContent,
  CardHeader,
  CardContent,
  CardActions,
  Loading,
  DataGrid,
  PermissionDialog,
} from "../../../../components";
import { value } from "../../../../helpers";
import {
  formatDate,
  formatPrice,
  responseErros,
  voltar,
} from "../../../../utils";
import { format } from "date-fns";
import {
  CaixaService,
  ContasReceberPagamentosService,
  notification,
} from "../../../../services";
import Swal from "sweetalert2";
import { useImportContext } from "../../../../contexts/import.context";
import { usePermissaoRetirarBaixa } from "../../../../components/permission-dialog/use-permissao-retirar-baixa";
import { useColunaFilial } from "../../../../components/dataGrid/use-coluna-filial";

const RetirarBaixaDialog = ({
  id,
  open,
  setOpen,
  getAtrasoDias,
  listaContas,
  contaReceber,
  pagamento,
  pagadorList,
  listaMeiosPagamentos,
  dadosPagamentoList,
  configuracoes,
}) => {
  const { initialCollapses } = useImportContext();
  const [listaBaixas, setListaBaixas] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [baixasSelecionadas, setBaixasSelecionadas] = useState([]);
  const [loading, setLoading] = useState(false);
  const { dialogPermissao, validarPermissaoRetirarBaixa } =
    usePermissaoRetirarBaixa();
  const contasReceberPagamentosService = new ContasReceberPagamentosService();
  const caixaService = new CaixaService();

  const dadosPagamentoColunas = [
    {
      field: "dataPagamento",
      headerName: "Data Pagamento",
      flex: 475,
      valueGetter: (params) => {
        return format(formatDate.received(params.value), "dd/MM/yyyy");
      },
    },
    {
      field: "horaPagamento",
      headerName: "Hora Pagamento",
      flex: 400,
      valueGetter: (params) => {
        return format(formatDate.received(params.value), "HH:mm");
      },
    },
    {
      field: "pagadorId",
      headerName: "Responsável",
      flex: 500,
      valueGetter: (params) => {
        const pagador = value.autoComplete(pagadorList, params.value);
        if (pagador) return pagador.nomeRazaoSocial;
        return params.value;
      },
    },
    {
      field: "bancoCaixaId",
      headerName: "Banco/Caixa",
      flex: 400,
      valueGetter: (params) => {
        const conta = listaContas.find((item) => item.id == params.value);
        if (!conta) return params.value;
        return conta.nome;
      },
    },
    {
      field: "atrasoDias",
      headerName: "Atraso",
      flex: 350,
      valueGetter: () => {
        return getAtrasoDias(
          contaReceber?.dataVencimento,
          pagamento?.dataPagamento,
        );
      },
    },
    {
      field: "meioPagamentoId",
      headerName: "Meio Pagamento",
      flex: 600,
      valueGetter: (params) => {
        const meioPagamento = listaMeiosPagamentos.find(
          (meio) => meio.id == params.value,
        );
        if (!meioPagamento) return params.value;
        return meioPagamento.descricao;
      },
    },
    {
      field: "tipoBaixa",
      headerName: "Tipo de Baixa",
      flex: 600,
      renderCell: (params) => {
        switch (params.value) {
          case "BAIXA_RENEGOCIACAO":
            return "Baixa por Renegociação";
          case "BAIXA_COMPENSACAO":
            return "Baixa por Compensação";
          default:
            return "Baixa Normal";
        }
      },
    },
    {
      field: "valor",
      headerName: "Valor Baixa",
      flex: 450,
      valueGetter: (params) => formatPrice(parseFloat(params.value)),
    },
  ];
  const colunas = useColunaFilial(dadosPagamentoColunas, open);

  const cancelarPagamentos = async () => {
    if (baixasSelecionadas?.length) {
      setLoading(true);

      let existeCaixaFechado = false;

      for (const baixa of baixasSelecionadas) {
        const result = await caixaService.caixaAberto(
          baixa.dataPagamento,
          baixa.bancoCaixaId,
          baixa.empresaId,
        );

        if (!result.isAxiosError) {
          const caixaAberto = result.data;
          if (!caixaAberto) {
            existeCaixaFechado = true;
            break;
          }
        } else {
          responseErros(result);
          setLoading(false);
          return;
        }
      }

      setLoading(false);

      if (existeCaixaFechado) {
        notification.alertaGenericos(
          "Não é possível retirar as baixas. Um ou mais caixas vinculados a estes recebimentos estão fechados.",
        );
        return;
      }

      const autorizado = await validarPermissaoRetirarBaixa();
      if (!autorizado) return;

      const retornoAlerta = await notification.confirmacaoGenericos({
        title: "Excluir!",
        text: "Tem certeza que deseja retirar as baixas?",
        icon: "warning",
        confirmButtonText: "EXCLUIR",
        cancelButtonText: "CANCELAR",
        showCancelButton: true,
      });

      if (retornoAlerta.isConfirmed) {
        setLoading(true);
        const res = await contasReceberPagamentosService.removerPagamentos(id, {
          excluirHistoricoPagamentos: configuracoes.excluirHistoricoPagamentos,
          pagamentos: baixasSelecionadas,
        });
        setLoading(false);
        if (!res.isAxiosError) {
          const retornoSucesso = await Swal.fire({
            title: "Baixas removidas com sucesso!",
            icon: "success",
            confirmButtonText: "OK",
          });
          if (retornoSucesso.isConfirmed) {
            initialCollapses();
            voltar();
          }
        } else {
          responseErros(res);
        }
      }
    } else {
      notification.alertaGenericos("Selecione pelo menos uma baixa");
    }
  };

  const handleSelecaoBaixas = (newRowSelectionModel) => {
    const notasSelecionadas = newRowSelectionModel.map((rowId) =>
      dadosPagamentoList.find((row) => row.id === rowId),
    );
    setBaixasSelecionadas(notasSelecionadas);
    setSelectionModel(newRowSelectionModel);
  };

  const onClose = () => {
    setSelectionModel([]);
    setBaixasSelecionadas([]);
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      const baixasReais = dadosPagamentoList.filter(
        (item) => parseFloat(item.valor) > 0 && !item.dataRetiradaBaixa && !item.deletedAt,
      );
      setListaBaixas(baixasReais);
    }
  }, [open, contaReceber]);

  return (
    <Dialog disableEscapeKeyDown maxWidth="xl" fullWidth open={open}>
      <DialogContent>
        <CardHeader title="Retirar Baixa" />
        <CardContent>
          <Grid container spacing={3}>
            <DataGrid
              checkboxSelection
              selectionModel={selectionModel}
              onRowSelectionModelChange={handleSelecaoBaixas}
              rows={listaBaixas}
              columns={colunas}
              rowCount={listaBaixas?.length}
              disableAcoes
            />
          </Grid>
        </CardContent>
        <CardActions className="d-flex justify-content-end">
          <Button variant="contained" color="padrao" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={cancelarPagamentos}
          >
            retirar baixa
          </Button>
        </CardActions>
      </DialogContent>
      <Loading loading={loading} />
      <PermissionDialog dialog={dialogPermissao} />
    </Dialog>
  );
};

export default RetirarBaixaDialog;
